/**
 * `ColumnDef` list for the Racks table, matched 1-1 to the legacy warehouse
 * grid: name link · code · location · status pill · U-height · power ·
 * bandwidth limit/usage · description. Read-only. Built on the shared
 * `TableLink` + rack status mapper.
 */
import type { ColumnDef } from '@vnetwork/ui/data';
import { StatusPill } from '@vnetwork/ui/data';
import type { Rack } from '@/api/capacities';
import { TableLink } from '@/components/table-link';
import { rackStatusView } from '../rack-mappers';

const muted = { color: 'var(--vn-fg-muted)', fontStyle: 'italic' } as const;
const mono = { fontFamily: 'var(--vn-font-mono)', fontSize: 'var(--vn-font-size-sm)' } as const;
const num = { display: 'block', textAlign: 'right' as const, fontVariantNumeric: 'tabular-nums' };

export const rackColumns: ColumnDef<Rack, unknown>[] = [
  {
    id: 'name',
    header: 'Name',
    cell: ({ row }) => (
      <TableLink to={`/capacities/racks/${row.original.id}`} title={row.original.name}>
        {row.original.name || row.original.code || row.original.id}
      </TableLink>
    ),
  },
  {
    id: 'code',
    header: 'Code',
    cell: ({ row }) =>
      row.original.code ? (
        <span style={mono}>{row.original.code}</span>
      ) : (
        <span style={muted}>—</span>
      ),
  },
  {
    id: 'location',
    header: 'Location',
    cell: ({ row }) =>
      row.original.location?.name ? (
        <span title={row.original.location.name} style={{ overflowWrap: 'anywhere' }}>
          {row.original.location.name}
        </span>
      ) : (
        <span style={muted}>—</span>
      ),
  },
  {
    id: 'status',
    header: 'Status',
    cell: ({ row }) => {
      const view = rackStatusView(row.original.status);
      return <StatusPill tone={view.tone}>{view.label}</StatusPill>;
    },
  },
  {
    id: 'u_height',
    header: 'U Height',
    cell: ({ row }) =>
      row.original.u_height ? (
        <span>{row.original.u_height}U</span>
      ) : (
        <span style={muted}>—</span>
      ),
  },
  {
    id: 'power_supply',
    header: 'Power (W)',
    cell: ({ row }) => <span style={num}>{row.original.power_supply ?? 0}</span>,
  },
  {
    id: 'bandwidth_limit',
    header: 'Bandwidth Limit',
    cell: ({ row }) => <span style={num}>{row.original.bandwidth_limit ?? 0} Mbps</span>,
  },
  {
    id: 'bandwidth_reality',
    header: 'Bandwidth Usage',
    cell: ({ row }) => <span style={num}>{row.original.bandwidth_reality ?? 0} Mbps</span>,
  },
  {
    id: 'description',
    header: 'Description',
    cell: ({ row }) =>
      row.original.description ? (
        <span
          title={row.original.description}
          style={{ display: 'block', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {row.original.description}
        </span>
      ) : (
        <span style={muted}>—</span>
      ),
  },
];
